import styled from '@emotion/styled';
import { Drawer as DrawerAntd, DrawerProps, Space, Tag } from 'antd';
import { capitalize } from 'lodash-es';
import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { CancelOrder, ReturnOrderListItemV2 } from 'services/api/refund';
import { useOpenReturnStatus } from 'hooks/business/openReturnStatus';
import {
  useRefundDrawerDataSource,
  useRefundDrawerTrade,
  useRefundDrawerPermissions,
  useRefundDrawerStatus,
  useRefundDrawerStatusTip,
} from './hooks';
import { RefundDrawerFooter } from './RefundDrawerFooter';
import { RefundDrawerBody } from './RefundDrawerBody';
import { TradeListContext } from 'views/Conversation/Sider/TradeContext';

export interface RefundDrawerProps extends DrawerProps {
  /* eslint-disable camelcase */
  // internal_buyer_id: number | string;
  // internal_store_id: number | string;
  // internal_trade_id: string;
  /* eslint-enable camelcase */
  // returnSn: string;
  returnItemData?: any;
  platform: General.Platform;
  row: ReturnOrderListItemV2 | CancelOrder;
  conversation?: Conversation.Conversation;
  onUpdate?: () => void;
  type: 'refund' | 'cancel';
  refreshList?: () => void;
}

const Drawer = styled(DrawerAntd)`
  ${({ theme }) => `.${theme.getAntdPrefixedClassName('drawer-body')}`} {
    padding: 0 0 10px 0;
  }
`;

const RefundDrawer = (props: RefundDrawerProps) => {
  const {
    visible = false,
    width = 720,
    platform,
    row,
    conversation,
    type,
    onUpdate,
    refreshList,
    ...drawerProps
  } = props;

  const { dataSource, isRefund } = useRefundDrawerDataSource(row, type);
  const { returnSn, refundAmount } = dataSource;
  const { trade, pullTrade, useTradeResult } = useRefundDrawerTrade({
    dataSource,
    visible,
    platform,
    conversation,
  });
  const { displaySensitive, operatable } = useRefundDrawerPermissions();
  const openReturn = useOpenReturnStatus();
  const { statusTagData, simpleStatus } = useRefundDrawerStatus({ dataSource, platform });

  const { t } = useTranslate();

  const returnRow = row as ReturnOrderListItemV2;
  const statusTip = useRefundDrawerStatusTip({
    platform,
    displayStatus: returnRow.displayStatus,
    unit: returnRow.unit,
    refundAmount: returnRow.refundAmount ?? dataSource.refundAmount,
    latestOfferAmount: returnRow.latestOfferAmount,
    compensationAmount: returnRow.compensationAmount,
    sellerEvidenceDeadlineMs: returnRow.sellerEvidenceDeadlineMs,
    returnShipDueDateMs: returnRow.returnShipDueDateMs,
    sellerCompensationDueDateMs: returnRow.sellerCompensationDueDateMs,
    dueDateMs: returnRow.dueDateMs,
  });

  const statusTag = useMemo(() => {
    if (!statusTagData) return null;
    return <Tag color={statusTagData.tagType}>{statusTagData.tagText}</Tag>;
  }, [statusTagData]);

  const footer = useMemo(
    () => (
      <RefundDrawerFooter
        trade={trade}
        operatable={operatable}
        platform={platform}
        isRefund={isRefund}
        openReturn={openReturn}
        returnSn={returnSn}
        dataSource={dataSource}
        simpleStatus={simpleStatus}
        conversation={conversation}
        pullTrade={pullTrade}
        onUpdate={onUpdate}
        refreshList={refreshList}
        row={row}
        type={type}
      />
    ),
    [
      trade,
      operatable,
      platform,
      isRefund,
      openReturn,
      returnSn,
      dataSource,
      simpleStatus,
      conversation,
      pullTrade,
      onUpdate,
      refreshList,
      row,
      type,
    ],
  );

  const bodyContent = useMemo(() => {
    if (!trade) return null;
    return (
      <RefundDrawerBody
        trade={trade}
        platform={platform}
        dataSource={dataSource}
        row={row}
        returnSn={returnSn}
        refundAmount={refundAmount}
        isRefund={isRefund}
        statusTip={statusTip}
        conversation={conversation}
        pullTrade={pullTrade}
        displaySensitive={displaySensitive}
      />
    );
  }, [
    trade,
    platform,
    dataSource,
    row,
    returnSn,
    refundAmount,
    isRefund,
    statusTip,
    conversation,
    pullTrade,
    displaySensitive,
  ]);

  return (
    <>
      <TradeListContext.Provider
        value={{
          ...useTradeResult,
          conversation,
        }}
      >
        <Drawer
          destroyOnClose
          title={
            isRefund ? (
              <Space>
                {t('scene.orderWorkspace.returnRefundTab.detail.section.refundDetail', {
                  platform: capitalize(platform),
                })}
                {statusTag}
              </Space>
            ) : (
              <Space>
                {t('scene.orderWorkspace.cancelOrderTab.detail.section.orderCancelDetail')}
                {statusTag}
              </Space>
            )
          }
          width={width}
          placement="right"
          visible={visible}
          footer={footer}
          mask={false}
          {...drawerProps}
        >
          {bodyContent}
        </Drawer>
      </TradeListContext.Provider>
    </>
  );
};
export default RefundDrawer;
